/**
 * Error Toast Utility
 *
 * Display error toasts with automatic translation and "View Details" action.
 * Can be called from anywhere (hooks, components, utilities).
 */

import { toast, type ExternalToast } from "sonner";
import i18n from "i18next";
import { translateError, translateErrorTitle } from "./translate-error";
import { useErrorModalStore } from "./error-modal-store";
import type { ApiError } from "./types";
import { getErrorCode } from "./types";

export interface ErrorToastOptions extends ExternalToast {
  /** Override the toast title (defaults to translated error title) */
  title?: string;
  /** Override the toast description (defaults to translated error message) */
  description?: string;
  /** Show "View Details" button to open error modal */
  showDetailsButton?: boolean;
}

/** Registry for error codes that should be intercepted before showing a toast. */
const errorInterceptors = new Map<string, (error: unknown) => void>();

/**
 * Register an interceptor for a specific error code.
 * When an error with this code is passed to showErrorToast, the interceptor
 * is called instead of showing the toast.
 */
export function registerErrorInterceptor(
  code: string,
  handler: (error: unknown) => void,
): () => void {
  errorInterceptors.set(code, handler);
  return () => {
    errorInterceptors.delete(code);
  };
}

export function showErrorToast(
  error: ApiError | Error | unknown,
  options: ErrorToastOptions = {},
): string | number {
  // Check if this error code has a registered interceptor
  const code = getErrorCode(error);
  if (code) {
    const interceptor = errorInterceptors.get(code);
    if (interceptor) {
      interceptor(error);
      return -1;
    }
  }

  const {
    title,
    description,
    showDetailsButton = true,
    ...toastOptions
  } = options;

  // Use translated error messages
  const errorTitle = title ?? translateErrorTitle(error);
  const errorDescription = description ?? translateError(error);

  // Translate "View Details" button label
  const viewDetailsLabel = i18n.t("errors.viewDetails", {
    defaultValue: "View Details",
  });

  // Build toast options
  const finalOptions: ExternalToast = {
    ...toastOptions,
    description: errorDescription,
    richColors: true,
  };

  // Add "View Details" action if enabled
  if (showDetailsButton && error instanceof Error) {
    finalOptions.action = {
      label: viewDetailsLabel,
      onClick: () => {
        useErrorModalStore.getState().openErrorModal(error);
      },
    };
  }

  return toast.error(errorTitle, finalOptions);
}

/**
 * Display a success toast.
 *
 * @example
 * ```ts
 * showSuccessToast("Document saved")
 * showSuccessToast("Document saved", { description: "Changes have been saved" })
 * ```
 */
export function showSuccessToast(
  message: string,
  options?: ExternalToast,
): string | number {
  return toast.success(message, {
    ...options,
    richColors: true,
  });
}

/**
 * Display a loading toast that can be updated later.
 *
 * @example
 * ```ts
 * const toastId = showLoadingToast("Uploading document...")
 * // Later:
 * toast.success("Upload complete!", { id: toastId })
 * // Or on error:
 * toast.error("Upload failed", { id: toastId })
 * ```
 */
export function showLoadingToast(
  message: string,
  options?: ExternalToast,
): string | number {
  return toast.loading(message, options);
}

/**
 * Dismiss a toast by ID.
 */
export function dismissToast(toastId?: string | number): void {
  toast.dismiss(toastId);
}
